<script setup lang="ts">
    // Chargebee card 6.6: move one subscription to Chargebee. The preview and its stop codes come from the API; the move itself is
    // queued for the Worker, so the page waits for BillingOwner to change.
    import { computed, reactive } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import Loading from '@shared/components/Loading.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import { ChargebeeMovePreview, getChargebeeMovePreview, requestChargebeeMove } from '../Subscription.access';
    import { getBillingOwner } from '../../Account/Account.access';

    const props = defineProps<{
        serviceSubscriptionId: string;
    }>();

    const emit = defineEmits(['moved']);

    const POLL_INTERVAL_MS = 3000;
    const POLL_ATTEMPTS = 40;

    // queued: a move was requested and is not yet confirmed — the move button stays off until the page is reloaded.
    // unconfirmed: the polling ran out (or was closed) before BillingOwner changed; the Worker may still be moving it.
    const state = reactive<{
        show: boolean;
        loading: boolean;
        queued: boolean;
        polling: boolean;
        unconfirmed: boolean;
        preview: ChargebeeMovePreview | null;
        termStart: string;
        termEnd: string;
    }>({
        show: false,
        loading: false,
        queued: false,
        polling: false,
        unconfirmed: false,
        preview: null,
        termStart: '',
        termEnd: '',
    });

    // A term by hand only where the orders cannot give it. Not for TERM_INVALID: a period invoiced in advance is moved once it
    // starts — the running period by hand would have Chargebee invoice it again.
    const TERM_BY_HAND_CODES = ['TERM_MISSING', 'TERM_LENGTH', 'TERM_EXPIRED'];
    const showTermFields = computed(
        () =>
            !!state.termStart ||
            !!state.termEnd ||
            (state.preview?.Blockers.some((b) => TERM_BY_HAND_CODES.includes(b.Code)) ?? false)
    );

    async function open(): Promise<void> {
        state.show = true;
        // A requested move is never offered again here: only its status can be checked.
        if (state.queued) {
            state.unconfirmed = !state.polling;
            return;
        }
        state.termStart = '';
        state.termEnd = '';
        await check();
    }

    async function check(): Promise<void> {
        state.loading = true;
        try {
            state.preview = await getChargebeeMovePreview(props.serviceSubscriptionId, state.termStart, state.termEnd);
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.loading = false;
        }
    }

    async function move(): Promise<void> {
        state.loading = true;
        try {
            state.preview = await requestChargebeeMove(props.serviceSubscriptionId, state.termStart, state.termEnd);
            if (state.preview.CanMove) {
                state.queued = true;
                waitForMove();
            }
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.loading = false;
        }
    }

    // The Worker sets BillingOwner last; a refusal there is logged, and the page then simply stays Ungapped-billed. Running out of
    // attempts is not a failure: the move may still be in the Billing queue.
    async function waitForMove(): Promise<void> {
        state.polling = true;
        state.unconfirmed = false;
        for (let attempt = 0; attempt < POLL_ATTEMPTS && state.polling; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            if (state.polling && (await isMoved())) {
                moved();
                return;
            }
        }
        state.polling = false;
        state.unconfirmed = true;
    }

    // Reads BillingOwner once; never requests the move again.
    async function checkStatus(): Promise<void> {
        state.loading = true;
        try {
            if (await isMoved()) moved();
            else bsAlert({ title: $Resources.BackOfficeChargebeeMoveStillPending, type: AlertTypes.Info });
        } finally {
            state.loading = false;
        }
    }

    async function isMoved(): Promise<boolean> {
        return (await getBillingOwner(props.serviceSubscriptionId).catch(() => '')) === 'Chargebee';
    }

    function moved(): void {
        state.show = false;
        state.queued = false;
        state.polling = false;
        state.unconfirmed = false;
        emit('moved');
    }

    function close(): void {
        state.show = false;
        state.polling = false;
    }

    function price(minorUnits: number | null): string {
        return minorUnits == null ? '' : formatMoney(minorUnits / 100, state.preview?.CurrencyCode ?? null, 2);
    }
</script>

<template>
    <button type="button" class="btn btn-default btn-sm m-sm-l" @click="open()">{{ $Resources.BackOfficeChargebeeMoveButton }}</button>

    <Modal :show="state.show" :title="$Resources.BackOfficeChargebeeMoveTitle" size="lg" @close="close()">
        <template #content>
            <Loading :show="state.loading" />

            <template v-if="state.preview">
                <p class="c-light">{{ $Resources.BackOfficeChargebeeMoveNoInvoice }}</p>

                <div v-if="state.queued && state.unconfirmed" class="alert alert-warning d-flex gap-sm align-items-center flex-wrap">
                    <span>{{ $Resources.BackOfficeChargebeeMoveNotConfirmed }}</span>
                    <button type="button" class="btn btn-default btn-sm" :disabled="state.loading" @click="checkStatus()">
                        {{ $Resources.BackOfficeChargebeeMoveCheckStatus }}
                    </button>
                </div>
                <div v-else-if="state.queued" class="alert alert-info">{{ $Resources.BackOfficeChargebeeMoveQueued }}</div>

                <div v-if="state.preview.Blockers.length > 0" class="alert alert-danger">
                    <strong>{{ $Resources.BackOfficeChargebeeMoveBlockers }}</strong>
                    <ul class="m-xs-t m-0-b">
                        <li v-for="issue in state.preview.Blockers" :key="issue.Code + issue.Message">
                            <code>{{ issue.Code }}</code> {{ issue.Message }}
                        </li>
                    </ul>
                </div>

                <div v-if="state.preview.Warnings.length > 0" class="alert alert-warning">
                    <ul class="m-0-b">
                        <li v-for="issue in state.preview.Warnings" :key="issue.Code">{{ issue.Message }}</li>
                    </ul>
                </div>

                <h4>{{ $Resources.BackOfficeChargebeeMoveCustomer }}</h4>
                <p v-if="state.preview.ChargebeeCustomerExists">
                    {{ $Resources.BackOfficeChargebeeMoveExists }}: <strong>{{ state.preview.ChargebeeCustomerId }}</strong>
                </p>
                <p v-if="state.preview.Customer">
                    {{ state.preview.Customer.Company }}<br />
                    {{ state.preview.Customer.Line1 }}<template v-if="state.preview.Customer.Line2">, {{ state.preview.Customer.Line2 }}</template>
                    <template v-if="state.preview.Customer.Line3">, {{ state.preview.Customer.Line3 }}</template><br />
                    {{ state.preview.Customer.Zip }} {{ state.preview.Customer.City }}, {{ state.preview.Customer.Country }}<br />
                    {{ state.preview.Customer.Email }}
                    <template v-if="state.preview.Customer.VatNumber"> | {{ $Resources.BackOfficeChargebeeMoveVatNumber }} {{ state.preview.Customer.VatNumber }}</template>
                    <template v-if="state.preview.Customer.NetTermDays"> | {{ ($Resources.BackOfficeChargebeeMoveNetTerm ?? '').replace('{0}', String(state.preview.Customer.NetTermDays)) }}</template>
                </p>

                <h4>{{ $Resources.BackOfficeChargebeeMoveSubscription }}</h4>
                <p v-if="state.preview.ChargebeeSubscriptionExists">
                    {{ $Resources.BackOfficeChargebeeMoveExists }}: <strong>{{ state.preview.ChargebeeSubscriptionId }}</strong>
                </p>
                <template v-else>
                    <p v-if="state.preview.Term">
                        {{ $Resources.BackOfficeChargebeeMoveTerm }}: <strong>{{ state.preview.Term.Start.substring(0, 10) }} – {{ state.preview.Term.End.substring(0, 10) }}</strong>
                        <span class="c-light m-xs-l">({{ state.preview.Term.Source }})</span>
                    </p>

                    <div v-if="showTermFields" class="d-flex gap-sm align-items-end flex-wrap m-md-b">
                        <label class="m-0-b">
                            {{ $Resources.BackOfficeChargebeeMoveTermStart }}
                            <input v-model="state.termStart" type="date" class="form-control" />
                        </label>
                        <label class="m-0-b">
                            {{ $Resources.BackOfficeChargebeeMoveTermEnd }}
                            <input v-model="state.termEnd" type="date" class="form-control" />
                        </label>
                        <button type="button" class="btn btn-default" :disabled="state.loading || !state.termStart || !state.termEnd" @click="check()">
                            {{ $Resources.BackOfficeChargebeeMoveCheck }}
                        </button>
                    </div>

                    <table v-if="state.preview.Lines.length > 0" class="table table-condensed">
                        <thead>
                            <tr>
                                <th>{{ $Resources.BackOfficeChargebeeMoveItem }}</th>
                                <th class="text-right">{{ $Resources.BackOfficeChargebeeMoveQuantity }}</th>
                                <th class="text-right">{{ $Resources.BackOfficeChargebeeMovePrice }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="line in state.preview.Lines" :key="line.ItemPriceId">
                                <td>{{ line.ItemPriceId }}</td>
                                <td class="text-right">{{ line.Quantity }}</td>
                                <td class="text-right">{{ price(line.UnitPrice) }}</td>
                            </tr>
                            <tr v-for="(discount, index) in state.preview.Discounts" :key="'discount' + index">
                                <td>
                                    {{ $Resources.BackOfficeChargebeeMoveDiscount }} ({{ discount.ItemPriceId ?? $Resources.BackOfficeChargebeeMoveDiscountOnInvoice }})
                                    <template v-if="discount.PeriodMonths">· {{ discount.PeriodMonths }} {{ $Resources.BackOfficeChargebeeMoveMonths }}</template>
                                </td>
                                <td></td>
                                <td class="text-right">{{ discount.Percentage != null ? `${discount.Percentage} %` : price(discount.Amount) }}</td>
                            </tr>
                            <tr v-for="limit in state.preview.LimitOverrides" :key="limit.FeatureId">
                                <td>{{ limit.FeatureId }}</td>
                                <td class="text-right">{{ limit.Value }}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <h4>{{ $Resources.BackOfficeChargebeeMoveInvoices }}</h4>
                <p>{{ state.preview.Invoices }} {{ $Resources.BackOfficeChargebeeMoveInvoiceCount }}, {{ state.preview.CreditNotes }} {{ $Resources.BackOfficeChargebeeMoveCreditNoteCount }}</p>
            </template>
        </template>

        <template #button>
            <ConfirmButton
                :buttonText="$Resources.BackOfficeChargebeeMoveConfirm"
                buttonClasses="btn-primary"
                :disabled="state.loading || state.queued || !state.preview?.CanMove"
                @confirm="move()" />
        </template>
    </Modal>
</template>
